<template>
  <section class="base__table">
    <!-- Switch :: View mode -->
    <section
      v-if="isMobile"
      class="switch mb-5"
      :class="{ 'is-list': view === 'list' }"
    >
      <section
        class="switch__item"
        @click="view = 'grid'"
      >
        <GridIcon
          class="switch__item__icon"
          :class="{ 'is-active': view === 'grid' }"
        />
      </section>

      <section
        class="switch__item"
        @click="view = 'list'"
      >
        <MenuIcon
          class="switch__item__icon"
          :class="{ 'is-active': view === 'list' }"
        />
      </section>
    </section>
    <!-- Table -->
    <section class="base__table__content">
      <table v-if="data.length" aria-label="Tabela de dados">
        <caption class="is-sr-only">
          Tabela de dados
        </caption>
        <thead v-if="!isMobile || (isMobile && view === 'list')">
          <th
            v-if="selectable && !isMobile"
            scope="col"
            class="align-center"
          >
            <BaseCheckbox
              :checked="selectedAll"
              @change="onChangeAll"
            />
          </th>
          <th v-if="hasVisibleActionsForAnyRow && !hasSelfAction" scope="col">
            Ações
          </th>
          <th
            v-for="(column, colIndex) in visibleHeaders"
            :key="colIndex"
            scope="col"
            :class="getAlignmentClass(column)"
          >
            <slot
              :name="`header-${column.name}`"
              :column="column"
            >
              {{ column.label }}
            </slot>
          </th>
        </thead>
        <tbody>
          <!-- eslint-disable-next-line -->
          <template
            v-for="(row, rowIndex) in data"
          >
            <!-- Desktop row -->
            <!-- eslint-disable-next-line -->
            <tr
              v-if="!isMobile || (isMobile && view === 'list')"
              :class="[{ hasDblClick }, { hasClick }, applyRowClass(row, rowIndex)]"
              @dblclick.stop="(event) => !!hasDblClick && $emit('dblClickAct', { ...row, event })"
              @click="(event) => !!hasClick && $emit('clickAct', { ...row, event })"
            >
              <!-- Selectable -->
              <td
                v-if="selectable && !isMobile"
                class="align-center"
              >
                <BaseCheckbox
                  :checked="selectedRows.includes(
                    selectable_by === 'index' ? rowIndex : row[selectable_by],
                  )"
                  @change="toggleRow(rowIndex, row)"
                />
              </td>

              <!-- Actions -->
              <td
                v-if="hasVisibleActionsForAnyRow && !hasSelfAction"
                class="actions-cell"
              >
                <section class="table-actions">
                  <button
                    v-for="(act, index) in actions.filter((action) => shouldShow(action, row))"
                    :key="index"
                    v-tooltip="act.showLabel ? '' : act.label"
                    class="button"
                    :class="[
                      `variant__${act.variant || 'default'}`,
                      { label: act.showLabel },
                    ]"
                    @click="act.action(row)"
                  >
                    <component
                      :is="act.icon"
                      v-if="act.icon"
                      :class="act.iconClass"
                      aria-hidden="true"
                    />
                    <span v-if="act.showLabel">
                      {{ act.label }}
                    </span>
                  </button>
                </section>
              </td>

              <!-- Columns -->
              <td
                v-for="(column, colIndex) in visibleHeaders"
                :key="colIndex"
                :class="getAlignmentClass(column)"
              >
                <slot
                  :name="column.name"
                  :row="row"
                  :index="rowIndex"
                  :value="row[column.name]"
                >
                  {{ row[column.name] }}
                </slot>
              </td>
            </tr>

            <!-- Mobile row (Customizable) -->
            <!-- eslint-disable-next-line -->
            <tr v-else>
              <td :colspan="visibleHeaders.length">
                <slot
                  name="mobile"
                  :row="row"
                  :index="rowIndex"
                >
                  <!-- Mobile :: Vertical Columns -->
                  <BaseVerticalTable
                    no-headers
                    no-border
                    no-items-border
                    text-align="right"
                    :show-header="false"
                    :headers="headers"
                    :data="row"
                  >
                    <template
                      v-for="column in headers"
                      #[column.name]="slotProps"
                    >
                      <slot
                        :name="column.name"
                        v-bind="slotProps"
                      />
                    </template>

                    <template #content-footer="{ data }">
                      <section class="is-flex is-justify-content-center mb-2">
                        <slot
                          :data="data"
                          name="mobile-footer"
                        />
                      </section>
                      <section class="table-actions table-actions__mobile">
                        <button
                          v-for="(act, index) in actions
                            .filter((action) => shouldShow(action, data))"
                          :key="index"
                          class="button"
                          title="Antifraude"
                          :class="[`variant__${act.variant || 'default'}`]"
                          @click="act.action(data)"
                        >
                          <component
                            :is="act.icon"
                            v-if="act.icon"
                            :class="act.iconClass"
                            aria-hidden="true"
                          />
                        </button>
                      </section>
                    </template>
                  </BaseVerticalTable>
                </slot>
              </td>
            </tr>

            <!-- eslint-disable-next-line -->
            <tr
              v-if="expandable && rowIndex === expandedRowIndex"
              class="expanded-row"
            >
              <td :colspan="headers.length">
                <slot
                  name="expanded"
                  :row="row"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <slot
        v-else
        name="no-data"
      >
        <div
          class="empty-state mt-5"
        >
          <p>
            Não encontramos nenhum registro.
          </p>
          <p class="is-bolder">
            Selecione um novo período para realizar uma nova busca.
          </p>
          <p class="font-sm">
            <span class="is-bolder">Lembre-se:</span>
            quando nenhum período for selecionado, o sistema busca por propostas de até 1 dia atrás.
          </p>
        </div>
      </slot>
    </section>
  </section>
</template>

<script>
/** Core */
import { mapGetters } from 'vuex';

export default {
  name: 'BaseTable',
  components: {
    BaseVerticalTable: () => import('@/components/Base/VerticalTable/index.vue'),
    BaseCheckbox: () => import('@/components/Base/Checkbox/index.vue'),
    GridIcon: () => import('@/assets/icons/Grid.vue'),
    MenuIcon: () => import('@/assets/icons/Menu.vue'),
  },
  props: {
    headers: {
      required: true,
      type: Array,
      default: () => [],
    },
    data: {
      required: true,
      type: Array,
      default: () => [],
    },
    actions: {
      required: false,
      type: Array,
      default: () => [],
    },
    expandable: {
      type: Boolean,
      required: false,
      default: false,
    },
    expandedRowIndex: {
      type: Number,
      required: false,
      default: null,
    },
    selectable: {
      type: Boolean,
      required: false,
      default: false,
    },
    selectable_by: {
      type: String,
      required: false,
      default: 'index',
    },
    hasClick: {
      type: Boolean,
      required: false,
      default: false,
    },
    hasDblClick: {
      type: Boolean,
      required: false,
      default: false,
    },
    hasSelfAction: {
      type: Boolean,
      required: false,
      default: false,
    },
    rowClass: {
      type: [Function, String, Object],
      required: false,
      default: '',
    },
  },
  data: () => ({
    view: 'list', // list or grid
    // selectedAll: false,
    selectedRows: [],
  }),
  computed: {
    ...mapGetters({
      isMobile: 'app/isMobile',
    }),
    hasActions() {
      return !!this.actions.length;
    },
    visibleHeaders() {
      return this.headers.filter((h) => {
        if (h.show === undefined) return true;

        if (typeof h.show === 'function') return h.show();

        return !!h.show;
      });
    },
    hasVisibleActionsForAnyRow() {
      return this.data.some((row) => this.actions.some((action) => this.shouldShow(action, row)));
    },
    selectedAll: {
      get() {
        return this.selectedRows.length === this.data.length;
      },
    },
  },
  watch: {
    selectedRows: {
      deep: true,
      handler(value) {
        this.$emit('selection', value);
      },
    },
  },
  methods: {
    toggleRow(index, row) {
      const value = this.selectable_by === 'index'
        ? index
        : row[this.selectable_by];

      if (this.selectedRows.includes(value)) {
        this.selectedRows = this.selectedRows.filter((i) => i !== value);
      } else {
        this.selectedRows.push(value);
      }
    },
    onChangeAll(value) {
      if (!value) {
        this.selectedRows = [];
        return;
      }

      this.selectedRows = this.data.map((item, index) => (this.selectable_by === 'index' ? index : item[this.selectable_by]));
    },

    getAlignmentClass({ align }) {
      if (align) return `align-${align}`;
      return 'align-left';
    },
    shouldShow(action, row) {
      const show = action?.show;

      if (show === undefined) return true;
      if (typeof show === 'boolean') return show;
      if (typeof show === 'function') return show(row);
      return false;
    },

    applyRowClass(row, rowIndex) {
      if (!this.rowClass) return {};

      if (typeof this.rowClass === 'function') {
        return this.rowClass(row, rowIndex);
      }

      return this.rowClass;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_textVariables.scss';

.base__table {
  * {
    box-sizing: border-box;
  }

  &__content {
    overflow: auto;
    width: 100%;
    max-height: 800px;
  }
}

table {
  border: 1px solid map-get($stone-types, "300");
  border-radius: 8px;
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;

  thead, tbody {
    border-radius: 8px;
  }

  thead th {
    padding: 8px 16px;
    background: map-get($stone-types, "50");
    border-bottom: 1px solid map-get($stone-types, "200");

    font: 500 14px 'Inter', sans-serif;
    line-height: 16px;
    color: map-get($stone-types, "500");
    text-wrap: nowrap;

    &:first-child {
      border-top-left-radius: 8px;
    }

    &:last-child {
      border-top-right-radius: 8px;
    }
  }
  tbody tr {
    &:nth-child(even):not(.expanded-row) {
      background: map-get($stone-types, "50");;
    }

    &.expanded-row {
      td {
        border-top: 1px solid map-get($stone-types, "300");
      }

      &:not(:last-child) td {
        border-bottom: 1px solid map-get($stone-types, "300");
      }
    }

    &.hasDblClick,
    &.hasClick {
      cursor: pointer;
    }

    &:last-child {
      td:first-child {
        border-bottom-left-radius: 8px;
      }
      td:last-child {
        border-bottom-right-radius: 8px;
      }
    }

    td {
      padding: 8px 16px;
      font: 500 14px 'Inter', sans-serif;
      line-height: 20px;
      color: map-get($stone-types, "500");
      vertical-align: middle;

      &.actions-cell {
        width: 1%;
        white-space: nowrap;
        text-align: right;
      }

      &.no-data {
        text-align: center;
      }
    }
  }

  .table-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    width: fit-content;

    &__mobile {
      margin-left: 0;
      justify-content: center;
      gap: 12px;
    }

    button {
      height: 30px;
      width: 30px;
      padding: 7px;
      display: grid;
      place-items: center;
      height: fit-content;
      border-radius: 4px;
      border: 1px solid map.get($stone-types, "300");

      &.variant__success {
        border: 1px solid #00A482;
        background: #00A482;
      }

      &.label {
        width: auto;
      }
    }
  }

  .align-left {
    text-align: left;
  }
  .align-center {
    text-align: center;
  }
  .align-right {
    text-align: right;
  }
}

.empty-state {
  text-align: center;

  p:last-of-type {
    font-style: italic;
  }
}

.switch {
  position: relative;
  display: flex;
  width: 100%;
  padding: 2px;
  border-radius: 8px;
  gap: 2px;
  background-color: $slate-50;

  // Elemento deslizante
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 2px;
    width: calc(50% - 2px); // dois itens
    height: calc(100% - 4px);
    background: white;
    border-radius: 6px;
    border: 1px solid $gray-200;
    box-shadow: 0px 1px 2px 0px #0000000D;

    transition: transform 0.25s ease;
  }

  &.is-list::before {
    transform: translateX(100%);
  }

  &__item {
    flex: 1;
    text-align: center;
    padding: 9px 0;
    position: relative;
    z-index: 2;
    cursor: pointer;

    &__icon {
      fill: #94A3B8;

      &.is-active {
        fill: #5666D5;
      }
    }
  }
}
</style>
